import type { ComboboxOption } from '../../../components/ui/combobox/types';
import { LANGUAGE_CODE_OPTIONS } from '../constants';
import type { LocaleEntry, LocaleGroup } from '../types/spark-options';

/** spark-js looks a locale up by exact key, so `EN` and `en` are the same language to us. */
export function normalizeLocale(locale: string): string {
  return locale.trim().toLowerCase();
}

export function nextId(rows: readonly { id: number }[]): number {
  return rows.reduce((highest, row) => Math.max(highest, row.id), -1) + 1;
}

/** Languages used by another group are disabled, so the same one can't be added twice. */
export function localeOptions(groups: LocaleGroup[], id: number): ComboboxOption[] {
  const used = new Set(
    groups
      .filter((group) => group.id !== id)
      .map((group) => normalizeLocale(group.locale))
      .filter(Boolean),
  );
  return LANGUAGE_CODE_OPTIONS.map((option) => ({
    ...option,
    disabled: used.has(normalizeLocale(option.value)),
  }));
}

/** Keys can repeat, so entries are always addressed by id. */
export function updateEntry(
  entries: LocaleEntry[],
  id: number,
  patch: Partial<Omit<LocaleEntry, 'id'>>,
): LocaleEntry[] {
  return entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry));
}

/** Named fields have a fixed key, so they read and write the first entry carrying it. */
export function namedEntry(entries: LocaleEntry[], key: string): LocaleEntry | undefined {
  return entries.find((entry) => entry.key === key);
}

export function entryValue(entries: LocaleEntry[], key: string): string {
  return namedEntry(entries, key)?.value ?? '';
}

export function setEntryValue(entries: LocaleEntry[], key: string, value: string): LocaleEntry[] {
  const existing = namedEntry(entries, key);
  if (existing) return updateEntry(entries, existing.id, { value });
  return [...entries, { id: nextId(entries), key, value }];
}

/** Keys used by another entry are disabled, so the same key can't be added twice. */
export function entryKeyOptions(
  entries: LocaleEntry[],
  id: number,
  options: ComboboxOption[],
): ComboboxOption[] {
  const used = new Set(
    entries
      .filter((entry) => entry.id !== id)
      .map((entry) => entry.key.trim())
      .filter(Boolean),
  );
  return options.map((option) => ({ ...option, disabled: used.has(option.value) }));
}
